import React, { FC } from 'react';
import { Attachments } from 'types/Attachments';
import { useLinkAttachmentsChange } from 'app/components/linkAttachments/hooks/useLinkAttachmentsChange';
import AttachLink from 'app/components/linkAttachments/components/AttachLink';
import { UserPermission } from 'constants/userPermission';
import UserPermissionGate from 'components/UserPermissionGate';
import EditableLinkCards from 'app/components/linkAttachments/components/EditableLinkCards';
import CallToActionSelect from 'app/components/linkAttachments/components/CallToActionSelect';
import SetUTMTagsForm from 'app/components/linkAttachments/components/SetUTMTagsForm';
import PostsConfig from 'app/config/posts';
import ProfileLogoAtTheEndToggle from 'app/components/linkAttachments/components/ProfileLogoAtTheEndToggle';
import FileSourceInput from 'app/components/FileSourceInput';
import LinkOwnershipVerification from 'app/components/linkAttachments/components/LinkOwnershipVerification';
import { useFileSource } from 'app/components/fileSourceInput/hooks/useFileSource';
import { AttachmentConvertor } from 'utils/attachments';
import { PostComposerFormOptions } from 'app/modules/posts/modules/post/components/composer/hooks/usePostComposerFormOptions';
import { UseSetStateAction } from 'utils/hooks/useSetState';

export type LinkAttachmentsProps = {
  page: {
    id: number;
    type: number;
  };
  attachments: Attachments;
  onAttachmentsChange: (
    fn: (attachments: Attachments) => Partial<Attachments>,
  ) => void;
  onOptionsChange?: UseSetStateAction<PostComposerFormOptions>;
  withLinkOwnership?: boolean;
  withMultiShareEndCard?: boolean;
  withDescriptionField?: boolean;
  isFullLinkEditable?: boolean;
  withCallToAction?: boolean;
  dataNamePrefix: string;
  maxCount?: number;
};

const LinkAttachments: FC<LinkAttachmentsProps> = ({
  onAttachmentsChange,
  onOptionsChange,
  attachments,
  page,
  maxCount = PostsConfig.MAX_FILES_TO_ACCEPT,
  ...props
}) => {
  const {
    updateAttachmentsCard,
    removeAttachmentCard,
    addAttachmentsCard,
    moveAttachmentIndex,
    setIsLinkOwnershipVerified,
    toggleMultiShareEndCard,
    clearAttachmentsCards,
  } = useLinkAttachmentsChange({ onAttachmentsChange, onOptionsChange });
  const filesSource = useFileSource({
    page,
    onChange: (mediaLibraryItems) =>
      mediaLibraryItems.forEach((media) => {
        const { medium, is_original_link_media } =
          AttachmentConvertor.fromMediaLibraryItem(media);

        addAttachmentsCard({ medium, is_original_link_media }, true);
      }),
    maxFiles: maxCount - attachments.cards.length,
    allowedFiles: ['image'],
    uploadSequence: 'fifo',
  });

  const hasLinkCards = attachments.cards.length > 0;

  return (
    <>
      <AttachLink
        dataNamePrefix={props.dataNamePrefix}
        onLinkFetched={addAttachmentsCard}
        onLinkRemove={hasLinkCards ? clearAttachmentsCards : undefined}
        pageId={page.id}
        link={attachments.cards[0]?.url}
      />

      {hasLinkCards && (
        <>
          {props.withLinkOwnership && attachments.cards[0]?.url && (
            <LinkOwnershipVerification
              pageId={page.id}
              url={attachments.cards[0]?.url}
              onVerifiedChange={setIsLinkOwnershipVerified}
            />
          )}
          <EditableLinkCards
            page={page}
            isEditable={
              attachments.cards.length > 1 ||
              (props.withLinkOwnership
                ? attachments.is_link_ownership_verified
                : true)
            }
            isImageRemovable={attachments.cards.length > 1}
            withDescriptionField={props.withDescriptionField}
            isFullLinkEditable={props.isFullLinkEditable}
            attachmentsCards={attachments.cards}
            updateAttachmentsCard={updateAttachmentsCard}
            removeAttachmentCard={removeAttachmentCard}
            moveAttachmentIndex={moveAttachmentIndex}
            runningUploads={filesSource.mediaUploader.runningUploads}
          />
          {maxCount > attachments.cards.length && (
            <UserPermissionGate
              scopes={UserPermission.MEDIA_LIBRARY_MANAGE_MEDIA}
            >
              <FileSourceInput
                fileSourceController={filesSource}
                variant="plus-button"
              />
            </UserPermissionGate>
          )}
          {props.withCallToAction && (
            <CallToActionSelect
              value={attachments.cards[0]?.call_to_action ?? null}
              onChange={(call_to_action) =>
                updateAttachmentsCard({ call_to_action })
              }
            />
          )}
          {props.withMultiShareEndCard && (
            <ProfileLogoAtTheEndToggle
              value={attachments.multi_share_end_card}
              onChange={toggleMultiShareEndCard}
            />
          )}
          <SetUTMTagsForm
            value={attachments.cards[0]?.url ?? ''}
            onChange={(url) => updateAttachmentsCard({ url })}
          />
        </>
      )}
    </>
  );
};

export default LinkAttachments;
